import _ from "lodash";
import { useContext } from "react";
import { DownloadIcon } from "lucide-react";

import { BulkOperationEnum } from "@/__generated__/graphql";
import { displayNameForView } from "@/app/app/_/commands/NavigateAppViewCommands";
import { useAppRouteState } from "@/app/app/_/routing";
import { Icon } from "@/components/atoms/Icon";
import { Mutations, Queries } from "@/constants";
import { useBackendMutation } from "@/hooks/useBackendMutation";
import {
  CONNECTION_VIEWS,
  connectionVariables,
  isConnectionView,
  type ConnectionViewConfig,
} from "@/hooks/useConnectionSetup";
import { Objects } from "@/objects";
import { toColumns } from "@/objects/@utils";
import { AppViewSubnavMenuContext } from "@/layouts/AppLayout/AppTopbar/AppViewSubnav";
import { State } from "@/state";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Export the list the view shows as a CSV: every row it would show - not
 * only the pages loaded - with the columns on screen. It is written in the
 * background, as the list's own query asked page after page, and arrives in
 * the asker's notifications.
 */
export function UtilityBarExport() {
  const { view, tab } = useAppRouteState();
  const { mutate, loading } = useBackendMutation({
    mutation: Mutations.REQUEST_BULK_OPERATION,
  });
  const isInMenu = useContext(AppViewSubnavMenuContext);

  if (!isConnectionView(view)) {
    return null;
  }
  const config = CONNECTION_VIEWS[view] as ConnectionViewConfig;
  const name = displayNameForView(view);

  const onExport = () => {
    const state = State.getState();
    void mutate({
      variables: {
        input: {
          operation: BulkOperationEnum.Export,
          name,
          connection: config.queryKey,
          // What the page asks for, so the file holds what it shows.
          variables: connectionVariables(view, {
            tab,
            filters: state.filters,
            sortables: state.sortables,
            entityIdentifiers: state.activeEntityIdentifiers,
          }),
          // Each column where its value is on the record; one the app works
          // out on screen is left out.
          columns: toColumns(Objects[config.typename])
            .filter((column) => column.exportPath !== null)
            .map((column) => ({
              key: column.objectKey,
              // A column the page heads with nothing - the entity's box - is
              // headed in the file by what it is.
              label: column.label || _.startCase(column.objectKey),
              path: column.exportPath ?? column.objectKey,
            })),
        },
      },
      refetchQueries: [Queries.INBOX_CONNECTION_QUERY],
      onCompleted: () =>
        state.setSnackbarMessage(
          `Exporting ${name.toLowerCase()}. It will be in your notifications when it is ready.`,
        ),
    });
  };

  // In the subnav's "⋯" menu there is room for its words beside its icon.
  if (isInMenu) {
    return (
      <Button
        variant="ghost"
        size="sm"
        data-testid="utility-bar-export"
        onClick={onExport}
        disabled={loading}
      >
        {loading ? <Spinner /> : <Icon ReactIcon={DownloadIcon} size="sm" />}
        Export as CSV
      </Button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon-sm"
          aria-label="Export as CSV"
          data-testid="utility-bar-export"
          onClick={onExport}
          className="shrink-0"
          disabled={loading}
        >
          {loading ? <Spinner /> : <Icon ReactIcon={DownloadIcon} size="md" />}
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">Export as CSV</TooltipContent>
    </Tooltip>
  );
}
